// import { RiGroup2Fill } from "react-icons/ri";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Form, Formik } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { BiShow, BiHide } from "react-icons/bi";

import { schema } from "./SignUpSideCurtains.utils";
import Toast from "../../common/Toast/Toast";
import AppConstants from "../../configs/AppConstants";
import EntityConstants from "../../configs/EntityConstants";
import useMaxLengthInput from "../../hooks/useMaxLengthInput";
import GoogleIcon from "../../resources/icons/Google-Social-icon.svg";
import MicrosoftIcon from "../../resources/icons/microsoft.svg";
import { POST, GET } from "../../service/CRUDService";
import { GLogin, microsoftAuth } from "../../service/OAuthLogin";
import AuthContext from "../../store/auth-context";
import { automationTestIds } from "../../testing/automationTestIds";

const FormValueSync = ({ values, setCachedValues }) => {
  useEffect(() => {
    setCachedValues(values);
  }, [values, setCachedValues]);
  return null;
};

const SignUpSideCurtains = ({
  variant = "modal",
  toggleComponent,
  closeModal,
  toggleIsMessage,
  cachedValues,
  setCachedValues,
  clearCache,
}) => {
  const { t } = useTranslation();

  const phobe_country_code = require("../../resources/json/phobe_country_code.json");
  const authContext = useContext(AuthContext);
  const [message, setMessage] = useState("");
  const [isSignOrg] = useState(false);
  const signUpScheme = useMemo(() => schema(t), [t]);
  const [hideShowpassword, setHideShowpassword] = useState("password");
  const [hideShowConfpassword, setHideShowConfpassword] = useState("password");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const [phoneInputError, setPhoneInputError] = useState("");
  const { limitReached, handleMaxLengthKeyDown, clearLimitReached } =
    useMaxLengthInput();
  const recaptchaRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaValue(null);
      }
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(EntityConstants.GET_ORGANISATION_DETAILS);
        const data = await response.json();

        if (response.ok) {
          setIsCaptchaRequired(data.recaptcha_flag === true);
        } else {
          Toast({
            type: "error",
            title: t("authPages:error"),
            message: t("authPages:failed_fetch_org_details"),
          });
          setIsCaptchaRequired(true);
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("authPages:error"),
          message: t("authPages:something_went_wrong"),
        });
        setIsCaptchaRequired(true);
      }
    };

    fetchData();
  }, []);

  const isLandingVariant = variant === "landing";

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setCaptchaValue(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (isCaptchaRequired && !captchaValue) {
        Toast({
          type: "error",
          title: t("authPages:captcha_error"),
          message: t("authPages:captcha_incomplete"),
        });
        return;
      }

      const user = { user_type: "Admin" };
      const { phone_no, country_code, ...restValues } = values;
      const requestBody = {
        ...restValues,
        ...user,
        ...AppConstants.ENTITY,
        ...(isCaptchaRequired && { captchaToken: captchaValue }),
      };
      if (phone_no?.trim()) {
        requestBody.phone_no = phone_no.trim();
        requestBody.country_code = country_code;
      }

      const response = await POST(EntityConstants.SIGNUPISSUER, requestBody);
      const data = await response.json();

      resetCaptcha();

      if (response.ok) {
        Toast({
          type: "success",
          title: t("authPages:success"),
          message: t("authPages:user_created_successfully"),
        });

        clearCache();
        setMessage(data.message);
        // Don't close model here; show "message" instead
        // let user sign-in
        // closeModal() //!remove closeModal in the final release if not needed

        return;
      } else if (response.status === 400) {
        toast(
          <div className="flex items-center gap-2">
            <p className="px-2 bg-cfPrimaryLight text-cfSecondary rounded-full">
              !
            </p>
            <div>
              <p className="text-cfPrimaryLight font-semibold">{data?.error}</p>
              <p className="text-cfPrimaryLight">{data?.message}</p>
            </div>
          </div>,
        );
      } else if (response.status === 404) {
        Toast({
          type: "error",
          title: t("authPages:error"),
          message: data?.message,
        });
      } else {
        Toast({
          type: "error",
          title: t("authPages:error"),
          message: data?.message,
        });
      }
    } catch (error) {
      resetCaptcha();

      // Handle unexpected errors
      console.error("Signup failed:", error);
      Toast({
        type: "error",
        title: t("authPages:error"),
        message: t("authPages:unexpected_error"),
      });
    }
  };

  useEffect(() => {
    message && toggleIsMessage();
  }, [message]);

  const signup = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      const login = await GLogin(credentialResponse, "success");
      if (login === true) {
        authContext.login() && authContext.googlelogin();
        closeModal();
      }
    },
    onError: (error) => {
      GLogin(error, "failed");
    },
  });

  const handleToggleComponent = () => {
    resetCaptcha();
    toggleComponent();
  };

  if (!message && !isSignOrg) {
    return (
      <Formik
        // enableReinitialize
        initialValues={cachedValues}
        validationSchema={signUpScheme}
        validateOnMount
        onSubmit={(values) => handleSubmit(values)}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isValid,
          isSubmitting,
        }) => {
          const labelClass = isLandingVariant
            ? "text-[13px] font-semibold text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-cfDashboardChartRevoked after:ml-0.5"
            : "my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left after:content-['*'] after:text-red-500 after:ml-0.5";
          const optionalLabelClass = isLandingVariant
            ? "text-[13px] font-semibold text-cfSignInSignUpInputLabelText text-left"
            : "my-auto font-medium text-sm text-cfSignInSignUpInputLabelText text-left";

          const fieldWrapperClass = isLandingVariant
            ? "flex flex-col gap-1.5 rounded-md bg-inherit w-full"
            : "flex flex-col gap-2 rounded-md bg-inherit w-full";

          const getInputClass = (hasError) =>
            `${isLandingVariant ? "!rounded-[8px] h-[46px] px-3.5 text-sm text-cfSignInSignUpHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText" : "rounded-md h-12 text-base text-cfSignInSignUpInputPlaceholderText"} w-full focus:outline-none border transition-colors duration-150 ${
              hasError
                ? "border-cfDashboardChartRevoked"
                : "border-cfSignInSignUpInputBorder"
            }`;

          const getInputStyle = () => ({
            borderRadius: isLandingVariant ? "8px" : "0.5rem",
          });

          return (
            <Form
              noValidate
              onSubmit={handleSubmit}
              className="flex w-full flex-col"
              id={automationTestIds.signupForm}
            >
              <FormValueSync
                values={values}
                setCachedValues={setCachedValues}
              />
              {isLandingVariant ? (
                <div className="mb-6 flex flex-col gap-1.5">
                  <h1
                    id={automationTestIds.signupModalHeader}
                    className="text-[30px] font-extrabold leading-tight text-cfSignInSignUpHeadingText"
                  >
                    {t("authPages:sign_up")}
                  </h1>
                  <p className="text-sm leading-5 text-cfSignInSignUpIntroMessageText">
                    {t("authPages:sign_up_personal_account")}
                  </p>
                </div>
              ) : (
                <h3
                  id={automationTestIds.signupModalHeader}
                  className="px-8 text-4xl font-medium leading-6 text-cfSignInSignUpHeadingText"
                >
                  {t("authPages:sign_up")}
                </h3>
              )}
              <div
                className={
                  isLandingVariant
                    ? "flex w-full flex-col font-CfPrimaryFont"
                    : "flex h-full w-full flex-col items-center justify-center overflow-hidden px-8 py-6 text-left font-CfPrimaryFont"
                }
              >
                {!isLandingVariant && (
                  <div className="w-full py-3">
                    <p className="text-left text-base text-cfSignUpToYourOrganisationAccountText">
                      {t("authPages:sign_up_personal_account")}
                    </p>
                  </div>
                )}
                <div
                  id="inputSignUp"
                  className={`flex w-full flex-col ${
                    isLandingVariant ? "gap-[0.625rem]" : "gap-[0.625rem]"
                  }`}
                >
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupFirstName}
                      className={labelClass}
                    >
                      {t("authPages:first_name")}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      id={automationTestIds.signupFirstName}
                      placeholder={t("authPages:first_name_placeholder")}
                      aria-invalid={Boolean(
                        limitReached.first_name ||
                        ((touched.first_name || values.first_name) &&
                          errors.first_name),
                      )}
                      className={getInputClass(
                        limitReached.first_name ||
                          ((touched.first_name || values.first_name) &&
                            errors.first_name),
                      )}
                      style={getInputStyle()}
                      maxLength={100}
                      value={values.first_name}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.value.length < 100)
                          clearLimitReached("first_name");
                      }}
                      onKeyDown={handleMaxLengthKeyDown}
                      onBlur={handleBlur}
                    />
                  </div>
                  <p
                    className="text-left text-cfDashboardChartRevoked"
                    role="alert"
                  >
                    {limitReached.first_name
                      ? t("authPages:max_100_characters")
                      : (touched.first_name || values.first_name) &&
                          errors.first_name
                        ? errors.first_name
                        : null}
                  </p>
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupLastName}
                      className={labelClass}
                    >
                      {t("authPages:last_name")}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      id={automationTestIds.signupLastName}
                      placeholder={t("authPages:last_name_placeholder")}
                      aria-invalid={Boolean(
                        limitReached.last_name ||
                        ((touched.last_name || values.last_name) &&
                          errors.last_name),
                      )}
                      className={getInputClass(
                        limitReached.last_name ||
                          ((touched.last_name || values.last_name) &&
                            errors.last_name),
                      )}
                      style={getInputStyle()}
                      maxLength={100}
                      value={values.last_name}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.value.length < 100)
                          clearLimitReached("last_name");
                      }}
                      onKeyDown={handleMaxLengthKeyDown}
                      onBlur={handleBlur}
                    />
                  </div>
                  <p
                    className="text-left text-cfDashboardChartRevoked"
                    role="alert"
                  >
                    {limitReached.last_name
                      ? t("authPages:max_100_characters")
                      : (touched.last_name || values.last_name) &&
                          errors.last_name
                        ? errors.last_name
                        : null}
                  </p>
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupEmail}
                      className={labelClass}
                    >
                      {t("authPages:email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      id={automationTestIds.signupEmail}
                      placeholder={t("authPages:email_placeholder")}
                      aria-invalid={Boolean(
                        limitReached.email ||
                        ((touched.email || values.email) && errors.email),
                      )}
                      className={getInputClass(
                        limitReached.email ||
                          ((touched.email || values.email) && errors.email),
                      )}
                      style={getInputStyle()}
                      maxLength={100}
                      value={values.email}
                      onChange={(e) => {
                        handleChange(e);
                        if (e.target.value.length < 100)
                          clearLimitReached("email");
                      }}
                      onKeyDown={handleMaxLengthKeyDown}
                      onBlur={handleBlur}
                    />
                  </div>
                  <p
                    className="text-left text-cfDashboardChartRevoked"
                    role="alert"
                  >
                    {limitReached.email
                      ? t("authPages:max_100_characters")
                      : (touched.email || values.email) && errors.email
                        ? errors.email
                        : null}
                  </p>
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupPhone}
                      className={optionalLabelClass}
                    >
                      {t("authPages:phone_optional")}
                    </label>
                    <div
                      className={`flex w-full items-center justify-between border border-cfSignInSignUpInputBorder ${
                        isLandingVariant
                          ? "h-[46px] rounded-[8px]"
                          : "mr-[.14rem] h-12 rounded-md"
                      }`}
                    >
                      <div className="flex items-center bg-cfSecondary rounded-l-lg h-10 w-auto min-w-[8rem]">
                        <select
                          type="country_code"
                          name="country_code"
                          id={automationTestIds.signupCountryCode}
                          placeholder={t("authPages:country_code")}
                          className={`border-l-0 border-t-0 border-b-0 border-r border-cfSignInSignUpInputBorder rounded-l-lg cf-text-base h-10 mr-[.14rem] pl-2 pr-1 focus:outline-none w-full`}
                          value={values.country_code}
                          onChange={(e) => {
                            handleChange(e);
                          }}
                        >
                          {phobe_country_code
                            .map(({ phone, emoji }) => ({
                              code: Array.isArray(phone) ? phone[0] : phone,
                              emoji,
                            }))
                            .filter(({ code }) => /^\+\d/.test(code))
                            .map(({ code, emoji }) => (
                              <option key={code} value={code}>
                                {emoji} {code}
                              </option>
                            ))}
                        </select>
                      </div>
                      <input
                        type="tel"
                        name="phone_no"
                        id={automationTestIds.signupPhone}
                        placeholder={t("authPages:phone_number")}
                        className=" border-none w-full h-10 mr-[.14rem] pl-2 focus:outline-none"
                        style={{ borderRadius: "5px" }}
                        value={values.phone_no}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const numericValue = rawValue.replace(/[^0-9]/g, "");
                          if (rawValue !== numericValue) {
                            setPhoneInputError(
                              t("authPages:invalid_phone_number"),
                            );
                          } else {
                            setPhoneInputError("");
                          }
                          handleChange({
                            target: { name: "phone_no", value: numericValue },
                          });
                        }}
                        onBlur={handleBlur}
                      />
                    </div>
                  </div>
                  <p className="text-left text-sm text-cfDashboardChartRevoked">
                    {errors.country_code &&
                      touched.country_code &&
                      `${errors.country_code}`}
                  </p>
                  <p className="text-left text-sm text-cfDashboardChartRevoked">
                    {phoneInputError ||
                      (errors.phone_no &&
                        touched.phone_no &&
                        `${errors.phone_no}`)}
                  </p>
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupPassword}
                      className={labelClass}
                    >
                      {t("authPages:password_label")}
                    </label>
                    <div
                      className={`flex w-full items-center justify-between border border-cfSignInSignUpInputBorder pl-2 ${
                        isLandingVariant
                          ? "h-[46px] rounded-[8px]"
                          : "mr-[.14rem] h-12 rounded-md"
                      }`}
                    >
                      <input
                        type={hideShowpassword}
                        name="password"
                        id={automationTestIds.signupPassword}
                        placeholder={t("authPages:password_placeholder")}
                        className={`w-full border-none focus:outline-none ${
                          isLandingVariant
                            ? "text-sm text-cfSignInSignUpHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText"
                            : "text-base text-cfSignInSignUpInputPlaceholderText"
                        }`}
                        maxLength={100}
                        value={values.password}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value.length < 100)
                            clearLimitReached("password");
                        }}
                        onKeyDown={handleMaxLengthKeyDown}
                        onBlur={handleBlur}
                      />
                      <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                        {hideShowpassword === "password" && (
                          <button
                            type="button"
                            id={automationTestIds.signupPasswordShow}
                            className="cursor-pointer bg-transparent border-none p-0"
                            aria-label={t("authPages:password_label")}
                            onClick={() => {
                              setHideShowpassword("text");
                              if (hideShowConfpassword === "text") {
                                setHideShowConfpassword("password");
                              }
                            }}
                          >
                            <BiShow className="cursor-pointer" />
                          </button>
                        )}
                        {hideShowpassword === "text" && (
                          <button
                            type="button"
                            id={automationTestIds.signupPasswordHide}
                            className="cursor-pointer bg-transparent border-none p-0"
                            aria-label={t("authPages:password_label")}
                            onClick={() => {
                              setHideShowpassword("password");
                            }}
                          >
                            <BiHide className="cursor-pointer" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p
                    className="text-left text-cfDashboardChartRevoked"
                    role="alert"
                  >
                    {limitReached.password
                      ? t("authPages:max_100_characters")
                      : errors.password && touched.password && errors.password}
                  </p>
                  <div className={fieldWrapperClass}>
                    <label
                      htmlFor={automationTestIds.signupConfirmPassword}
                      className={labelClass}
                    >
                      {t("authPages:confirm_password")}
                    </label>
                    <div
                      className={`flex w-full items-center justify-between border border-cfSignInSignUpInputBorder pl-2 ${
                        isLandingVariant
                          ? "h-[46px] rounded-[8px]"
                          : "mr-[.14rem] h-12 rounded-md"
                      }`}
                    >
                      <input
                        type={hideShowConfpassword}
                        name="confirm_password"
                        id={automationTestIds.signupConfirmPassword}
                        placeholder={t("authPages:confirm_password")}
                        className={`w-full border-none focus:outline-none ${
                          isLandingVariant
                            ? "text-sm text-cfSignInSignUpHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText"
                            : "text-base text-cfSignInSignUpInputPlaceholderText"
                        }`}
                        maxLength={100}
                        value={values.confirm_password}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value.length < 100)
                            clearLimitReached("confirm_password");
                        }}
                        onKeyDown={handleMaxLengthKeyDown}
                        onBlur={handleBlur}
                      />
                      <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                        {hideShowConfpassword === "password" && (
                          <button
                            type="button"
                            id={automationTestIds.signupConfirmPasswordShow}
                            className="cursor-pointer bg-transparent border-none p-0"
                            aria-label={t("authPages:confirm_password")}
                            onClick={() => {
                              setHideShowConfpassword("text");
                              if (hideShowpassword === "text") {
                                setHideShowpassword("password");
                              }
                            }}
                          >
                            <BiShow className="cursor-pointer" />
                          </button>
                        )}
                        {hideShowConfpassword === "text" && (
                          <button
                            type="button"
                            id={automationTestIds.signupConfirmPasswordHide}
                            className="cursor-pointer bg-transparent border-none p-0"
                            aria-label={t("authPages:confirm_password")}
                            onClick={() => setHideShowConfpassword("password")}
                          >
                            <BiHide className="cursor-pointer" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p
                    className="text-left text-cfDashboardChartRevoked"
                    role="alert"
                  >
                    {limitReached.confirm_password
                      ? t("authPages:max_100_characters")
                      : errors.confirm_password &&
                        (touched.confirm_password || values.confirm_password) &&
                        `${errors.confirm_password}`}
                  </p>
                </div>
                <div
                  className={`w-full flex flex-col ${
                    isLandingVariant ? "mt-6 gap-4" : "my-3 gap-[0.875rem]"
                  }`}
                >
                  <div>
                    <button
                      type="submit"
                      id={automationTestIds.signupSubmit}
                      disabled={
                        !isValid ||
                        isSubmitting ||
                        (isCaptchaRequired && !captchaValue)
                      }
                      aria-busy={isSubmitting}
                      className={`inline-flex w-full items-center justify-center gap-2 transition-colors duration-200 ${
                        isLandingVariant
                          ? "h-12 rounded-[8px] text-[15px] font-bold"
                          : "h-11 rounded-xl px-5 py-1 text-base font-semibold"
                      } text-cfSecondary ${
                        isSubmitting
                          ? "cursor-wait bg-cfButton"
                          : isValid && (!isCaptchaRequired || captchaValue)
                            ? "cursor-pointer bg-cfButton hover:bg-cfPrimaryDark"
                            : "cursor-not-allowed bg-cfDisabled"
                      }`}
                    >
                      {isSubmitting && (
                        <svg
                          className="animate-spin h-4 w-4 shrink-0 text-cfSecondary"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                      {isSubmitting
                        ? t("authPages:signing_up")
                        : t("authPages:sign_up")}
                    </button>
                  </div>
                  {isCaptchaRequired && (
                    <div className="flex custom-recaptcha-width justify-center items-center">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                        onChange={handleCaptchaChange}
                      />
                    </div>
                  )}
                  <div
                    className={`flex w-full items-center gap-2.5 text-cfSignInSignUpOrText ${
                      isLandingVariant ? "text-xs font-semibold" : ""
                    }`}
                  >
                    {isLandingVariant ? (
                      <>
                        <div className="h-px flex-1 bg-cfCardBorder" />
                        <span>{t("authPages:or")}</span>
                        <div className="h-px flex-1 bg-cfCardBorder" />
                      </>
                    ) : (
                      <>
                        <hr className="w-1/2" />
                        <p>{t("authPages:or")}</p>
                        <hr className="w-1/2" />
                      </>
                    )}
                  </div>
                  <div
                    className={`flex ${isLandingVariant ? "gap-2" : "gap-4"}`}
                  >
                    <button
                      type="button"
                      id={automationTestIds.signupGoogle}
                      onClick={signup}
                      className={
                        isLandingVariant
                          ? "flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-cfSignInSignUpInputBorder bg-cfSecondary px-2"
                          : "flex h-11 w-full items-center justify-center gap-2 rounded-full border"
                      }
                    >
                      <img
                        src={GoogleIcon}
                        alt=""
                        className={`size-4 ${isLandingVariant ? "shrink-0" : ""}`}
                      />
                      <span
                        className={`font-semibold font-CfPrimaryFont ${
                          isLandingVariant
                            ? "whitespace-nowrap text-[11px] leading-none text-cfSignInSignUpHeadingText sm:text-xs"
                            : "text-base"
                        }`}
                      >
                        {isLandingVariant
                          ? t("authPages:sign_up_with_google")
                          : t("authPages:sign_up")}
                      </span>
                    </button>
                    <button
                      type="button"
                      id={automationTestIds.signupMicrosoft}
                      onClick={microsoftAuth}
                      className={
                        isLandingVariant
                          ? "flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-cfSignInSignUpInputBorder bg-cfSecondary px-2"
                          : "flex h-11 w-full items-center justify-center gap-2 rounded-full border"
                      }
                    >
                      <img
                        src={MicrosoftIcon}
                        alt=""
                        className={`size-4 ${isLandingVariant ? "shrink-0" : ""}`}
                      />
                      <span
                        className={`font-semibold font-CfPrimaryFont ${
                          isLandingVariant
                            ? "whitespace-nowrap text-[11px] leading-none text-cfSignInSignUpHeadingText sm:text-xs"
                            : "text-base"
                        }`}
                      >
                        {isLandingVariant
                          ? t("authPages:sign_up_with_microsoft")
                          : t("authPages:sign_up")}
                      </span>
                    </button>
                  </div>
                </div>
                <div
                  className={
                    isLandingVariant
                      ? "mt-8 flex items-center justify-center gap-1 text-sm text-cfDontHaveAccountText"
                      : "my-3 flex px-0.5 text-sm text-[1rem] leading-[1.2rem]"
                  }
                >
                  <p className="text-cfDontHaveAccountText">
                    {t("authPages:have_account", "Already have an account?")}
                  </p>
                  &nbsp;
                  <button
                    type="button"
                    id={automationTestIds.signupSwitchToSignin}
                    onClick={handleToggleComponent}
                    className={`flex focus:outline-none ${
                      isLandingVariant ? "font-bold underline" : ""
                    }`}
                  >
                    <p className="font-semibold text-cfDontHaveAccountSignUpText hover:text-cfPrimaryDark">
                      {t("authPages:sign_in")}
                    </p>
                  </button>
                </div>
              </div>
              {/* <div className="bg-cfSignUpBottomSection h-full w-full flex flex-col items-center gap-3 px-8 py-6 rounded-b-[1.25rem]">
              <p className="text-cfSignUpAreYouAnAdministratorText text-sm">
                Are you an admin?
              </p>
              <button
                onClick={signup}
                className="flex items-center gap-2 justify-center w-full h-11 border rounded-xl bg-cfSecondary"
              >
                Create your organisation
              </button>
            </div> */}
            </Form>
          );
        }}
      </Formik>
    );
  } else if (message && !isSignOrg) {
    return (
      <div
        className="w-full min-w-full px-8 py-6"
        id={automationTestIds.signupSuccessPanel}
      >
        <div className={`bg-sky-200 rounded-md p-2 text-center shadow-md`}>
          {message}
        </div>
        <div className="flex justify-center text-[1rem] leading-[1.2rem] mb-2">
          <button
            type="button"
            id={automationTestIds.signupSwitchToSigninAfterSuccess}
            onClick={handleToggleComponent}
            className="flex m-3 bg-cfPrimaryLight hover:bg-cfPrimaryLight rounded-lg text-cfSecondary px-2 py-1"
          >
            <p>{t("authPages:sign_in")}</p>
          </button>
        </div>
      </div>
    );
  } else if (!message && isSignOrg) {
    /*Orginasation Registration Flow*/
  }
};

export default SignUpSideCurtains;
